import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { ApiError, Button, Card, CardContent, CardHeader, CardTitle, Input, Label, StatusChip } from '@smena/ui';
import { PageHeader } from '@/components/PageHeader';
import { QueryState } from '@/components/QueryState';
import {
  fetchLlmSettings,
  reindexKb,
  saveLlmSettings,
  sourceLabel,
  testLlm,
  type LlmField,
  type LlmSettingsView,
  type ProbeResult,
  type SaveLlmInput,
} from '@/features/llm/api';

/**
 * Раздел «LLM»: провайдер, ключ, модели, таймаут, rpm - без ssh на стенд и рестарта контейнера.
 * Постановка: .docs/admin-llm-settings.md.
 *
 * Форма держит собственное состояние, а не правит кеш запроса: пока админ печатает, фоновое
 * обновление не должно затирать введённое. Синхронизация - один раз на каждый новый снимок.
 */
export function LlmPage() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['llm-settings'], queryFn: fetchLlmSettings, staleTime: 5_000 });

  return (
    <>
      <PageHeader
        title="LLM"
        subtitle="Провайдер, ключи и модели конвейера. Изменения применяются на лету, без перезапуска."
      />
      <QueryState isLoading={q.isLoading} error={q.error}>
        {q.data ? (
          <div className="flex flex-col gap-4">
            <StatusPanel data={q.data} />
            <SettingsForm
              data={q.data}
              onSaved={() => void qc.invalidateQueries({ queryKey: ['llm-settings'] })}
            />
          </div>
        ) : null}
      </QueryState>
    </>
  );
}

/** Верхняя плашка: что реально крутится сейчас и что мешает настройкам подействовать. */
function StatusPanel({ data }: { data: LlmSettingsView }) {
  const qc = useQueryClient();
  const reindex = useMutation({
    mutationFn: reindexKb,
    onSuccess: (r) => {
      toast.success(`Пересчитано ${r.updated} из ${r.total} чанков моделью ${r.model}`);
      void qc.invalidateQueries({ queryKey: ['llm-settings'] });
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Не удалось пересчитать'),
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle>Сейчас в работе</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-3 text-sm">
        <div className="flex flex-wrap items-center gap-4">
          <span className="flex items-center gap-2">
            <span className="text-muted">Чат:</span>
            <StatusChip tone={data.chat.configured ? 'green' : 'red'}>
              {data.chat.configured ? (data.runtime.chatModel ?? 'настроен') : 'не настроен - L0'}
            </StatusChip>
          </span>
          <span className="flex items-center gap-2">
            <span className="text-muted">Embeddings:</span>
            <StatusChip tone={data.embed.configured ? 'green' : 'amber'}>
              {data.embed.configured ? data.runtime.embedModel : 'stub - слабый поиск по базе'}
            </StatusChip>
          </span>
        </div>

        {/* Рубильники стенда специально не перебиваются из UI: смысл рубильника в том, что
            его нельзя снять оттуда же, откуда управляют настройками. Но молчать о них нельзя -
            иначе «сохранил, а ничего не изменилось» выглядит как баг. */}
        {data.envOverrides.mock ? (
          <Banner tone="amber">
            В окружении задан <code>MOCK=1</code> - конвейер работает на mock-правилах, и настройки
            ниже на него не влияют. Снимается только в <code>.env</code> стенда.
          </Banner>
        ) : null}
        {data.envOverrides.stub ? (
          <Banner tone="amber">
            В окружении задан <code>LLM_STUB=1</code> - вместо провайдера отвечает встроенный стаб.
          </Banner>
        ) : null}

        {data.kb.stale > 0 ? (
          <Banner tone="amber">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <span>
                {data.kb.stale} из {data.kb.chunks} фрагментов базы знаний посчитаны другой моделью.
                Поиск по ним даёт шум - без ошибок в логах, просто нерелевантные ответы.
              </span>
              <Button
                size="sm"
                variant="outline"
                disabled={reindex.isPending}
                onClick={() => reindex.mutate()}
              >
                {reindex.isPending ? 'Пересчитываем…' : 'Пересчитать'}
              </Button>
            </div>
          </Banner>
        ) : null}

        {data.updatedAt ? (
          <p className="text-xs text-muted">
            Последнее изменение: {new Date(data.updatedAt).toLocaleString('ru-RU')}
            {data.updatedByEmail ? ` · ${data.updatedByEmail}` : ''}
          </p>
        ) : (
          <p className="text-xs text-muted">
            Настройки в админке ещё не сохранялись - всё берётся из <code>.env</code> стенда.
          </p>
        )}
      </CardContent>
    </Card>
  );
}

interface FormState {
  baseUrl: string;
  apiKey: string;
  model: string;
  timeoutMs: string;
  rateLimitRpm: string;
  thinking: string;
  proxyUrl: string;
  embedBaseUrl: string;
  embedApiKey: string;
  embedModel: string;
}

function toForm(d: LlmSettingsView): FormState {
  return {
    baseUrl: d.chat.baseUrl ?? '',
    // Ключи не приходят с сервера и не должны: поля всегда пустые, пустое = «не менять».
    apiKey: '',
    model: d.chat.model ?? '',
    timeoutMs: String(d.chat.timeoutMs),
    rateLimitRpm: String(d.chat.rateLimitRpm),
    thinking: d.chat.thinking ?? '',
    proxyUrl: '',
    embedBaseUrl: d.embed.baseUrl ?? '',
    embedApiKey: '',
    embedModel: d.embed.model ?? '',
  };
}

function SettingsForm({ data, onSaved }: { data: LlmSettingsView; onSaved: () => void }) {
  const [form, setForm] = useState<FormState>(() => toForm(data));
  const [probe, setProbe] = useState<Record<'chat' | 'embed', ProbeResult | null>>({
    chat: null,
    embed: null,
  });

  // Синхронизируемся с сервером по updatedAt, а не на каждый рендер: иначе автообновление
  // запроса затирало бы то, что человек печатает прямо сейчас.
  useEffect(() => {
    setForm(toForm(data));
  }, [data.updatedAt]);

  const set = (k: keyof FormState) => (v: string) => setForm((f) => ({ ...f, [k]: v }));

  const save = useMutation({
    mutationFn: (input: SaveLlmInput) => saveLlmSettings(input),
    onSuccess: () => {
      toast.success('Настройки сохранены и применены');
      onSaved();
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Не удалось сохранить'),
  });

  const test = useMutation({
    mutationFn: (kind: 'chat' | 'embed') =>
      testLlm({
        kind,
        ...(kind === 'chat'
          ? { baseUrl: form.baseUrl, apiKey: form.apiKey, model: form.model }
          : { baseUrl: form.embedBaseUrl, apiKey: form.embedApiKey, model: form.embedModel }),
        proxyUrl: form.proxyUrl,
      }).then((r) => ({ kind, r })),
    onSuccess: ({ kind, r }) => setProbe((p) => ({ ...p, [kind]: r })),
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Проверка не удалась'),
  });

  function submit(e: FormEvent): void {
    e.preventDefault();
    save.mutate({
      baseUrl: form.baseUrl,
      model: form.model,
      timeoutMs: Number(form.timeoutMs),
      rateLimitRpm: Number(form.rateLimitRpm),
      thinking: form.thinking,
      embedBaseUrl: form.embedBaseUrl,
      embedModel: form.embedModel,
      // Ключи и прокси уходят только если их ввели: пустое поле - это «не трогай»,
      // иначе открыть форму и нажать «Сохранить» означало бы стереть рабочий ключ.
      ...(form.apiKey.trim() ? { apiKey: form.apiKey } : {}),
      ...(form.embedApiKey.trim() ? { embedApiKey: form.embedApiKey } : {}),
      ...(form.proxyUrl.trim() ? { proxyUrl: form.proxyUrl } : {}),
    });
  }

  return (
    <form onSubmit={submit} className="flex flex-col gap-4">
      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Чат-модель</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-3">
            <Field label="Base URL" hint="Адрес OpenAI-совместимого API, обычно с /v1 на конце" source={data.sources.baseUrl}>
              <Input value={form.baseUrl} onChange={(e) => set('baseUrl')(e.target.value)} placeholder="https://routerai.ru/api/v1" />
            </Field>
            <Field
              label="Ключ"
              hint={data.chat.keyMasked ? `Сейчас: ${data.chat.keyMasked}. Пусто - оставить как есть` : 'Ключ не задан'}
              source={data.sources.apiKey}
            >
              <Input type="password" autoComplete="new-password" value={form.apiKey} onChange={(e) => set('apiKey')(e.target.value)} placeholder="не менять" />
            </Field>
            <Field label="Модель" source={data.sources.model}>
              <Input value={form.model} onChange={(e) => set('model')(e.target.value)} placeholder="openai/gpt-oss-120b" />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Таймаут, мс" source={data.sources.timeoutMs}>
                <Input inputMode="numeric" value={form.timeoutMs} onChange={(e) => set('timeoutMs')(e.target.value)} />
              </Field>
              <Field label="Лимит, запросов/мин" hint="0 - без локального ограничения" source={data.sources.rateLimitRpm}>
                <Input inputMode="numeric" value={form.rateLimitRpm} onChange={(e) => set('rateLimitRpm')(e.target.value)} />
              </Field>
            </div>
            <Field label="thinking" hint="Отправляется только в DeepSeek, остальные игнорируют" source={data.sources.thinking}>
              <Input value={form.thinking} onChange={(e) => set('thinking')(e.target.value)} placeholder="disabled" />
            </Field>
            <ProbeRow
              result={probe.chat}
              pending={test.isPending && test.variables === 'chat'}
              onTest={() => test.mutate('chat')}
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Embeddings (поиск по базе знаний)</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-3">
            <Field label="Base URL" hint="Может быть другой провайдер: у чат-эндпоинта embedding-модели часто нет" source={data.sources.embedBaseUrl}>
              <Input value={form.embedBaseUrl} onChange={(e) => set('embedBaseUrl')(e.target.value)} placeholder="https://routerai.ru/api/v1" />
            </Field>
            <Field
              label="Ключ"
              hint={data.embed.keyMasked ? `Сейчас: ${data.embed.keyMasked}. Пусто - оставить как есть` : 'Ключ не задан'}
              source={data.sources.embedApiKey}
            >
              <Input type="password" autoComplete="new-password" value={form.embedApiKey} onChange={(e) => set('embedApiKey')(e.target.value)} placeholder="не менять" />
            </Field>
            <Field label="Модель" source={data.sources.embedModel}>
              <Input value={form.embedModel} onChange={(e) => set('embedModel')(e.target.value)} placeholder="baai/bge-m3" />
            </Field>
            <Field label="Размерность вектора" hint="Зашита в схему базы - меняется только миграцией" source={data.sources.embedDim}>
              <Input value={String(data.embed.dim)} readOnly disabled />
            </Field>
            <Field label="SOCKS5-прокси" hint="Только для LLM-запросов. Пусто - оставить как есть" source={data.sources.proxyUrl}>
              <Input
                type="password"
                autoComplete="new-password"
                value={form.proxyUrl}
                onChange={(e) => set('proxyUrl')(e.target.value)}
                placeholder={data.chat.proxyUrl ?? 'не используется'}
              />
            </Field>
            <ProbeRow
              result={probe.embed}
              pending={test.isPending && test.variables === 'embed'}
              onTest={() => test.mutate('embed')}
            />
          </CardContent>
        </Card>
      </div>

      <div className="flex items-center gap-3">
        <Button type="submit" disabled={save.isPending}>
          {save.isPending ? 'Сохраняем…' : 'Сохранить и применить'}
        </Button>
        <span className="text-xs text-muted">
          Применяется сразу ко всем каналам. Смена embedding-модели потребует пересчёта базы знаний.
        </span>
      </div>
    </form>
  );
}

function Field({
  label,
  hint,
  source,
  children,
}: {
  label: string;
  hint?: string;
  source?: LlmSettingsView['sources'][LlmField];
  children: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-baseline justify-between gap-2">
        <Label>{label}</Label>
        {/* Бейдж источника: без него правка, перебитая значением из .env, выглядит как
            неработающее сохранение. */}
        {source ? (
          <span className="rounded bg-panel2 px-1.5 py-0.5 text-[10px] uppercase tracking-wider text-muted">
            {sourceLabel(source)}
          </span>
        ) : null}
      </div>
      {children}
      {hint ? <p className="text-xs text-muted">{hint}</p> : null}
    </div>
  );
}

function ProbeRow({
  result,
  pending,
  onTest,
}: {
  result: ProbeResult | null;
  pending: boolean;
  onTest: () => void;
}) {
  return (
    <div className="mt-1 flex flex-col gap-2 border-t border-line pt-3">
      <div className="flex items-center gap-3">
        <Button type="button" variant="outline" size="sm" disabled={pending} onClick={onTest}>
          {pending ? 'Проверяем…' : 'Проверить соединение'}
        </Button>
        {result ? (
          <StatusChip tone={result.ok ? 'green' : 'red'}>
            {result.ok ? `${result.ms} мс` : `ошибка${result.status ? ` ${result.status}` : ''}`}
          </StatusChip>
        ) : null}
      </div>
      {result ? (
        <p className={`text-xs ${result.ok ? 'text-muted' : 'text-destructive'}`}>
          {result.ok ? result.detail : result.error}
        </p>
      ) : null}
    </div>
  );
}

function Banner({ tone, children }: { tone: 'amber' | 'red'; children: ReactNode }) {
  const cls =
    tone === 'amber'
      ? 'border-amber/30 bg-amber/10 text-amber'
      : 'border-destructive/40 bg-destructive/10 text-destructive';
  return <div className={`rounded-md border px-3 py-2 text-xs ${cls}`}>{children}</div>;
}
